import PropTypes from 'prop-types';
import React, { useState } from 'react';

import CosignerDetails from '.././CosignerDetails';
import WhiteCard from '../../../../components/WhiteCard';
import { PROVISIONAL_OFFER_COSIGNER_DOCUMENT_MODAL } from '../../../../constants/heapEvents';
import { track } from '../../../../lib/heapHelpers';
import Button from '../../../Button';
import CosignerDocumentModal from '../CosignerDocumentModal';
import CosignerDocumentSection from '../CosignerDocumentSection';
import CostOfLoan from '../CostOfLoan';
import InterestRate from '../InterestRate';
import LoanTerm from '../LoanTerm';
import OfferFAQ from '../OfferFAQ';

import './index.scss';

const OfferDetails = ({
  data,
  offerAccepted,
  offerOptions,
  selectedOffer,
  hasCosigner,
  onSelectedOfferChange,
  offerIssuedDuringPromoPeriod,
  cosignerSelection,
  onCosignerSelectionChange,
  showDetails,
  onShowDetailsClick,
  customiseLoanComponentRef,
}) => {
  const differenceInMonths = (date1, date2) => {
    const year1 = date1.getFullYear();
    const year2 = date2.getFullYear();
    const month1 = date1.getMonth();
    const month2 = date2.getMonth();

    return (year2 - year1) * 12 + (month2 - month1);
  };

  const futureLoanPreApproved = Number.parseFloat(data.future_loan_pre_approved);

  const gracePeriodInMonths = differenceInMonths(
    new Date(data.first_disbursement_date),
    new Date(data.first_repayment_date),
  );

  const getOfferOptionsByLoanType = (data, loanType) =>
    offerOptions.filter(option => option.loan_type === loanType).length;

  const hasCosignerOffer = getOfferOptionsByLoanType(data, 'cosigner_inschool') > 0;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    track(PROVISIONAL_OFFER_COSIGNER_DOCUMENT_MODAL, {
      'Application GUID': data.id,
      'Customer GUID': data.customer_guid,
      'Open Document Modal': true,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="offer-details" data-testid="offer-details">
      {/* Remove component completely on refactor */}
      {/* <CustomiseLoan
        firstRepaymentDate={data.first_repayment_date}
        loan_amount={data.loan_amount}
        selectedOffer={selectedOffer}
        cosignerSelection={cosignerSelection}
        hasCosignerOffer={hasCosignerOffer}
        customiseLoanComponentRef={customiseLoanComponentRef}
      /> */}
      {hasCosignerOffer && (
        <>
          <CosignerDetails
            hasCosigner={hasCosigner}
            explanationOpen={true}
            cosignerSelection={cosignerSelection}
            onCosignerSelectionChange={onCosignerSelectionChange}
            disabled={offerAccepted}
          />
          {cosignerSelection === 'cosigner' && (
            <CosignerDocumentSection onClick={handleOpenModal} />
          )}
          {isModalOpen && (
            <CosignerDocumentModal onClose={handleCloseModal} title="Co-signer requirements" />
          )}
        </>
      )}
      <LoanTerm
        explanationOpen={true}
        offerOptions={offerOptions}
        selectedOffer={selectedOffer}
        onSelectedOfferChange={onSelectedOfferChange}
        applicationId={data.id}
        customerGuid={data.customer_guid}
        cosignerSelection={cosignerSelection}
        offerAccepted={offerAccepted}
      />
      {futureLoanPreApproved > 0 && (
        <WhiteCard className={'offer-details-container__white-card'}>
          <div className="offer-details-container__padding">
            <div className="offer-details-container__text-container">
              <div className="offer-details-container__text calendar-icon">
                Subsequent period(s)
              </div>
            </div>

            <div className="offer-details-container__text-container">
              <div className="offer-details-container__text currency-icon">
                USD <div className="inline-bold">{futureLoanPreApproved.toLocaleString()}</div>
              </div>
              <div className="offer-details-container__text__small tab-text">
                {data.living_expense_amount > 0
                  ? 'for tuition fees & living expense'
                  : 'for tuition fees'}
              </div>
            </div>

            <div className="offer-details-container__details-info">
              <div className="offer-details-container__text inline-bold thumb-up-icon">
                Loan Pre-approved
              </div>
              <div className="offer-details-container__text__small tab-text">
                You're pre-approved for this loan amount for subsequent period(s). You must apply
                before the start of each period to have access to the funds.
              </div>
            </div>
          </div>
        </WhiteCard>
      )}
      {!showDetails && !offerAccepted && (
        <Button className="detailed-offer-button" onClick={onShowDetailsClick}>
          Show detailed offer
        </Button>
      )}
      {(showDetails || offerAccepted) && (
        <div className="offer-details__detailed-offer-wrapper">
          <h3 className="section-title">Loan Details</h3>
          <InterestRate
            explanationOpen={false}
            term={selectedOffer.term_in_months / 12}
            margin={selectedOffer.fixed_margin}
            baseRate={selectedOffer.base_rate}
            interestRate={selectedOffer.interest_rate}
            disbursementDate={data.first_disbursement_date}
            offerIssuedDuringPromoPeriod={offerIssuedDuringPromoPeriod}
          />
          <CostOfLoan
            explanationOpen={false}
            offerAccepted={offerAccepted}
            currency={data.loan_currency}
            loanAmount={data.loan_amount}
            adminFee={data.admin_fee}
            adminFeePercentage={data.admin_fee_percentage}
            selectedOffer={selectedOffer}
            processingFeeValue={data.processing_fee_value}
            applicationId={data.id}
            customerGuid={data.customer_guid}
            cosignerSelection={cosignerSelection}
            gracePeriodInMonths={gracePeriodInMonths}
          />
          <OfferFAQ
            applicationId={data.id}
            customerGuid={data.customer_guid}
            restOfWorld={data.rest_of_world_course}
            processingFee={data.processing_fee_value}
            hasLivingExpense={parseInt(data.living_expense_amount > 0)}
          />
        </div>
      )}
    </div>
  );
};

OfferDetails.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    customer_guid: PropTypes.string.isRequired,
    loan_amount: PropTypes.string.isRequired,
    living_expense_amount: PropTypes.string,
    rest_of_world_course: PropTypes.bool,
    loan_currency: PropTypes.string.isRequired,
    first_repayment_date: PropTypes.string,
    first_disbursement_date: PropTypes.string,
    admin_fee: PropTypes.string,
    admin_fee_percentage: PropTypes.string,
    processing_fee_value: PropTypes.string,
    future_loan_pre_approved: PropTypes.string,
  }).isRequired,
  offerAccepted: PropTypes.bool,
  offerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      term_in_months: PropTypes.number.isRequired,
      fixed_margin: PropTypes.string.isRequired,
      base_rate: PropTypes.string.isRequired,
      interest_rate: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedOffer: PropTypes.shape({
    term_in_months: PropTypes.number.isRequired,
    fixed_margin: PropTypes.string.isRequired,
    base_rate: PropTypes.string.isRequired,
    interest_rate: PropTypes.string.isRequired,
  }).isRequired,
  hasCosigner: PropTypes.bool.isRequired,
  onSelectedOfferChange: PropTypes.func.isRequired,
  offerIssuedDuringPromoPeriod: PropTypes.bool.isRequired,
  cosignerSelection: PropTypes.string.isRequired,
  onCosignerSelectionChange: PropTypes.func.isRequired,
  showDetails: PropTypes.bool.isRequired,
  onShowDetailsClick: PropTypes.func.isRequired,
  customiseLoanComponentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default OfferDetails;
